import React, { useEffect, useState } from 'react';
import { authFetch } from '../../utils/api'; // ✅ import helper
import { parseUtc } from '../../utils/datetime';

function getDateLabel(isoString) {
  if (!isoString) return 'Unknown Date';
  // Values arrive as UTC DATETIME strings; parsing them as local would put
  // late-evening rows under the wrong day heading.
  const d = parseUtc(isoString);
  if (!d) return 'Unknown Date';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const fmt = (date) => date.toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
  if (d.toDateString() === today.toDateString()) return `Today — ${fmt(d)}`;
  if (d.toDateString() === yesterday.toDateString()) return `Yesterday — ${fmt(d)}`;
  return fmt(d);
}

function Stage2Table({ refresh }) {
  const [leads, setLeads] = useState([]);
  const [dmFilter, setDmFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const leadsPerPage = 10;

  const fetchLeads = async () => {
    try {
      const data = await authFetch('/leads');
      setLeads(data);
    } catch (error) {
      console.error('Failed to fetch leads:', error.message);
    }
  };

  const deleteLead = async (username) => {
    try {
      await authFetch(`/leads/${username}`, { method: 'DELETE' });
      fetchLeads();
    } catch (error) {
      console.error('Failed to delete lead:', error.message);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [refresh]);

  const filteredLeads = leads.filter((lead) => {
    let dmMatch = true;
    if (dmFilter === 'sent') dmMatch = lead.dmSent === true;
    if (dmFilter === 'unsent') dmMatch = lead.dmSent === false;
    let sourceMatch = true;
    if (sourceFilter !== 'all') sourceMatch = lead.source === sourceFilter;
    return dmMatch && sourceMatch;
  });

  const totalPages = Math.ceil(filteredLeads.length / leadsPerPage);
  const paginatedLeads = filteredLeads.slice(
    (currentPage - 1) * leadsPerPage,
    currentPage * leadsPerPage
  );

  // Build rows: inject a date-group header row whenever the date changes
  const rows = [];
  let lastDateLabel = null;
  paginatedLeads.forEach((lead, index) => {
    const dateLabel = getDateLabel(lead.timestamp || lead.scraped_at);
    if (dateLabel !== lastDateLabel) {
      // Count how many leads in filteredLeads share this date
      const groupCount = filteredLeads.filter(l =>
        getDateLabel(l.timestamp || l.scraped_at) === dateLabel
      ).length;
      rows.push(
        <tr key={`date-${dateLabel}`} className="date-group-header">
          <td colSpan={10}>
            <span className="date-group-label">{dateLabel}</span>
            <span className="date-group-count">{groupCount} lead{groupCount !== 1 ? 's' : ''}</span>
          </td>
        </tr>
      );
      lastDateLabel = dateLabel;
    }
    const globalIndex = (currentPage - 1) * leadsPerPage + index + 1;
    rows.push(
      <tr key={lead.username}>
        <td>{globalIndex}</td>
        <td>@{lead.username}</td>
        <td>{lead.followers || 0}</td>
        <td>{lead.phone || '—'}</td>
        <td>{lead.whatsapp || '—'}</td>
        <td>
          {lead.externalLink ? (
            <a href={lead.externalLink} target="_blank" rel="noreferrer" title={lead.externalLink}>🔗</a>
          ) : '—'}
        </td>
        <td>{lead.source || 'Instagram'}</td>
        <td>
          <a href={lead.profileUrl} target="_blank" rel="noreferrer">View</a>
        </td>
        <td>{lead.dmSent ? 'Yes' : 'No'}</td>
        <td>
          <button onClick={() => deleteLead(lead.username)} className="delete-btn">Delete</button>
        </td>
      </tr>
    );
  });

  return (
    <div className="table-container">
      {/* Table Heading + Controls */}
      <div className="table-header">
        <h2 className='stage2-header'>Stage 2: Review Leads</h2>
        <div className="table-controls">
          <select value={dmFilter} onChange={(e) => { setDmFilter(e.target.value); setCurrentPage(1); }} className="filter-btn">
            <option value="all">All DMs</option>
            <option value="sent">DM Sent</option>
            <option value="unsent">Not Sent</option>
          </select>

          <select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1); }} className="filter-btn">
            <option value="all">All Sources</option>
            <option value="Instagram">Instagram</option>
            <option value="FB Ads">FB Ads</option>
          </select>

          <button className="export-btn">Export Leads</button>
        </div>
      </div>

      {paginatedLeads.length === 0 ? (
        <p>No leads found.</p>
      ) : (
        <>
          <table className="lead-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Username</th>
                <th>Followers</th>
                <th>Phone</th>
                <th>WhatsApp</th>
                <th>Website</th>
                <th>Source</th>
                <th>Visit Profile</th>
                <th>DM Sent</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="page-indicator">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Stage2Table;
